"use strict";
// Copyright (c) 2020 WiseTime. All rights reserved.
Object.defineProperty(exports, "__esModule", { value: true });
exports.retry = exports.responseNotOk = exports.never = exports.withExponentialDelay = void 0;
var rxjs_1 = require("rxjs");
var operators_1 = require("rxjs/operators");
/**
 * Calculate delay value for exponential backoff strategy.
 * Based on the backoff-rxjs lib.
 * @see {@link https://github.com/alex-okrushko/backoff-rxjs/blob/2e98471e445d338662a218c6aa065e1dd9a18d6c/src/utils.ts#L7|backoff-rxjs}
 */
var exponentialBackoff = function (attempt, interval, maxInterval) {
    var backoffInterval = Math.pow(2, attempt) * interval;
    return Math.min(backoffInterval, maxInterval);
};
/**
 * Apply a delay to an Observable, that increases exponentially with the number of retry attempts.
 *
 * @param initialDelay - Initial delay that is applied on first retry.
 * @param maxDelay - Maximum delay to apply. Defaults to 1 hour.
 */
var withExponentialDelay = function (initialDelay, maxDelay) { return function (run) { return function (attempt, error) {
    return run(error)
        .pipe((0, operators_1.delay)(exponentialBackoff(attempt, initialDelay, maxDelay || 3600000)), (0, operators_1.catchError)(function (e) { return (0, rxjs_1.throwError)(e); }));
}; }; };
exports.withExponentialDelay = withExponentialDelay;
/**
 * A retry policy that never retries calls.
 */
exports.never = {
    shouldRetry: function () { return false; },
    maxRetries: 0,
    beforeRetry: function () { return (0, rxjs_1.of)(undefined); },
};
/**
 * Configure a retry policy that will retry calls when the server returns a non-OK gRPC status.
 * Note that calls that fail for other reasons (e.g. network failure) will not be retried.
 */
var responseNotOk = function (shouldRetry, maxRetries, beforeRetry) {
    if (maxRetries === void 0) { maxRetries = 2; }
    if (beforeRetry === void 0) { beforeRetry = function () { return (0, rxjs_1.of)(undefined); }; }
    return ({
        shouldRetry: shouldRetry,
        maxRetries: maxRetries,
        beforeRetry: beforeRetry,
    });
};
exports.responseNotOk = responseNotOk;
var isGrpcError = function (error) {
    if (!error) {
        return false;
    }
    var grpcError = error;
    return "code" in grpcError && "message" in grpcError && "metadata" in grpcError;
};
/**
 * Add automatic retry to a gRPC call {@link Observable}.
 *
 * - If the error does not match the retry policy's shouldRetry condition, the call is not retried and
 *   the Observable fails with the error from the gRPC call
 * - If the error is retryable and exceeds the maximum retry attempts, the call fails with the error
 * - If the provided beforeRetry() Observable fails then the call fails with the error from beforeRetry
 * - Otherwise the call from the source observable is retried with a configurable exponential backoff
 *
 * @param retryPolicy - a RetryPolicy that specifies whether and when to retry
 */
var retry = function (retryPolicy) { return function (source) {
    return source.pipe((0, operators_1.retryWhen)(function (errors) {
        return errors.pipe((0, operators_1.switchMap)(function (error, attempt) {
            if (!isGrpcError(error)) {
                return (0, rxjs_1.throwError)(error);
            }
            return (0, rxjs_1.iif)(function () { return attempt < retryPolicy.maxRetries && retryPolicy.shouldRetry(error); }, retryPolicy.beforeRetry(attempt, error).pipe((0, operators_1.catchError)(function (e) { return e
                ? (0, rxjs_1.throwError)(e)
                : (0, rxjs_1.throwError)(new Error("beforeRetry failed with undefined error")); })), (0, rxjs_1.throwError)(error));
        }));
    }));
}; };
exports.retry = retry;
